import { useNavigate } from 'react-router-dom';
import { CheckCheck, Trash2, Loader2 } from 'lucide-react';
import { AppLayout } from '@/components/layout/app-layout';
import { Button } from '@/components/ui/button';
import { NotificationList } from '@/components/notifications';
import { useI18n } from '@/lib/i18n';
import {
    useNotifications,
    useMarkNotificationRead,
    useMarkAllNotificationsRead,
    useDeleteNotification,
    useDeleteAllNotifications,
    useNotificationSubscription,
} from '@/hooks/use-notifications';
import type { Notification } from '@/lib/schemas';

export function NotificationsPage() {
    const navigate = useNavigate();
    const { t } = useI18n();

    // Subscribe to real-time notifications
    useNotificationSubscription();

    // Fetch all notifications
    const { data, isLoading } = useNotifications({ limit: 100 });

    // Mutations
    const markRead = useMarkNotificationRead();
    const markAllRead = useMarkAllNotificationsRead();
    const deleteNotification = useDeleteNotification();
    const deleteAll = useDeleteAllNotifications();

    const handleNotificationClick = (notification: Notification) => {
        // Mark as read
        if (!notification.isRead) {
            markRead.mutate(notification.id);
        }

        // For project invites, don't navigate - user must click the "View Invitation" button
        // This prevents navigating to a project the user hasn't joined yet
        if (notification.type === 'PROJECT_INVITE') {
            return;
        }

        // Navigate based on notification type
        if (notification.data) {
            const { projectId, taskId } = notification.data;
            if (projectId && taskId) {
                navigate(`/projects/${projectId}?task=${taskId}`);
            } else if (projectId) {
                navigate(`/projects/${projectId}`);
            }
        }
    };

    const unreadCount = data?.unreadCount || 0;
    const notifications = data?.notifications || [];

    return (
        <AppLayout >
            <div className="p-4 sm:p-6 md:p-8">
                <div className="max-w-3xl mx-auto">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8 rounded-xl backdrop-blur-md bg-white/5 border border-white/10 p-4 sm:p-6">
                        <div>
                            <h1 className="text-xl sm:text-2xl font-bold text-white">
                                {t('notifications.title')}
                            </h1>
                            <p className="text-white/70 mt-1 text-sm sm:text-base">
                                {unreadCount > 0
                                    ? `${unreadCount} ${t('notifications.title').toLowerCase()}`
                                    : t('notifications.noNotifications')}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            {unreadCount > 0 && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => markAllRead.mutate()}
                                    disabled={markAllRead.isPending}
                                    className="flex-1 sm:flex-none"
                                >
                                    {markAllRead.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    ) : (
                                        <CheckCheck className="h-4 w-4 mr-2" />
                                    )}
                                    {t('notifications.markAllRead')}
                                </Button>
                            )}
                            {notifications.length > 0 && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => deleteAll.mutate()}
                                    disabled={deleteAll.isPending}
                                    className="text-destructive hover:text-destructive flex-1 sm:flex-none"
                                >
                                    {deleteAll.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    ) : (
                                        <Trash2 className="h-4 w-4 mr-2" />
                                    )}
                                    <span className="hidden sm:inline">{t('common.clear')} {t('common.all').toLowerCase()}</span>
                                    <span className="sm:hidden">{t('common.clear')}</span>
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Notification list */}
                    <div className="backdrop-blur-md bg-white/5 rounded-lg border border-white/10">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-white/50" />
                            </div>
                        ) : (
                            <NotificationList
                                notifications={notifications}
                                onMarkRead={(id) => markRead.mutate(id)}
                                onDelete={(id) => deleteNotification.mutate(id)}
                                onNotificationClick={handleNotificationClick}
                                emptyMessage={t('notifications.noNotifications')}
                            />
                        )}
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}
